import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useMatchXg } from '@/features/matches/hooks';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { formatFullDate, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeCompetition, localizeRound } from '@/lib/i18n/footballNames';

interface Row {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: string;
}

function InfoRow({ row, last }: { row: Row; last: boolean }) {
  const palette = useThemePalette();
  return (
    <View className={`flex-row items-center gap-3 px-4 py-3 ${last ? '' : 'border-b border-line'}`}>
      <View className="h-7 w-7 items-center justify-center rounded-full bg-inset">
        <Ionicons name={row.icon} size={13} color={palette.soft} />
      </View>
      <AppText variant="micro" weight="medium" className="text-muted">
        {row.label}
      </AppText>
      <View className="flex-1" />
      <AppText variant="caption" weight="medium" className="text-ink" numberOfLines={1}>
        {row.value}
      </AppText>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Weather
// ---------------------------------------------------------------------------

/** Map a free-text weather description (and code as a fallback) to an Ionicon.
 *  The provider sends English keywords like "cloudy" / "light rain" / "clear". */
function weatherIcon(description: string, code: number): keyof typeof Ionicons.glyphMap {
  const d = description.toLowerCase();
  if (/(thunder|storm)/.test(d)) return 'thunderstorm-outline';
  if (/snow|sleet|ice/.test(d)) return 'snow-outline';
  if (/(rain|drizzle|shower)/.test(d)) return 'rainy-outline';
  if (/(fog|mist|haze)/.test(d)) return 'cloud-outline';
  if (/wind/.test(d)) return 'thunderstorm-outline';
  if (/(part).*cloud|cloud.*sun|sun.*cloud/.test(d)) return 'partly-sunny-outline';
  if (/(overcast|cloud)/.test(d)) return 'cloudy-outline';
  if (/(clear|sun|fair)/.test(d)) return 'sunny-outline';
  // Fall back on the WMO-style code bands when the text is unfamiliar.
  if (code >= 95) return 'thunderstorm-outline';
  if (code >= 71) return 'snow-outline';
  if (code >= 51) return 'rainy-outline';
  if (code >= 45) return 'cloud-outline';
  if (code >= 2) return 'partly-sunny-outline';
  if (code >= 1) return 'cloudy-outline';
  return 'sunny-outline';
}

/** Translate the raw English weather DESCRIPTION via a small keyword map; falls
 *  back to the provider's own string when nothing matches (so we never blank). */
function localizeWeather(description: string, t: (key: string) => string): string {
  const d = description.toLowerCase().trim();
  const keys: { test: RegExp; key: string }[] = [
    { test: /(thunder|storm)/, key: 'thunderstorm' },
    { test: /snow|sleet|ice/, key: 'snow' },
    { test: /(part).*cloud|cloud.*sun|sun.*cloud/, key: 'partlyCloudy' },
    { test: /(rain|drizzle|shower)/, key: 'rain' },
    { test: /(fog|mist|haze)/, key: 'fog' },
    { test: /(overcast)/, key: 'overcast' },
    { test: /(cloud)/, key: 'cloudy' },
    { test: /(clear|sun|fair)/, key: 'clear' },
    { test: /wind/, key: 'windy' },
  ];
  for (const { test, key } of keys) {
    if (test.test(d)) {
      const translated = t(`match.matchInfo.weatherDesc.${key}`);
      // i18next returns the key path itself on a miss — keep the raw string then.
      if (translated && !translated.endsWith(`.${key}`)) return translated;
    }
  }
  return description;
}

function WeatherSection({
  weather,
  bordered,
}: {
  weather: NonNullable<NonNullable<ReturnType<typeof useMatchXg>['data']>['stats']>['weather'];
  bordered: boolean;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  if (!weather) return null;

  const temp = Math.round(weather.temperature_c);
  const wind = Math.round(weather.wind_speed);
  const desc = localizeWeather(weather.description, t);

  return (
    <View className={`flex-row items-center gap-3 px-4 py-3 ${bordered ? 'border-t border-line' : ''}`}>
      <View
        className="h-10 w-10 items-center justify-center rounded-full bg-volt-glass"
        style={{ borderWidth: 1, borderColor: palette.voltLine }}
      >
        <Ionicons
          name={weatherIcon(weather.description, weather.code)}
          size={20}
          color={palette.voltBright}
        />
      </View>
      <View className="flex-1">
        <View className="flex-row items-baseline gap-1.5">
          <AppText variant="heading" weight="bold" tabular className="text-ink">
            {t('match.matchInfo.degrees', { value: localizeDigits(String(temp), arabicNumerals) })}
          </AppText>
          <AppText variant="caption" weight="medium" className="text-soft" numberOfLines={1}>
            {desc}
          </AppText>
        </View>
        <AppText variant="micro" className="text-muted" numberOfLines={1}>
          {t('match.matchInfo.wind', { value: localizeDigits(String(wind), arabicNumerals) })}
        </AppText>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Venue
// ---------------------------------------------------------------------------

/** A small pill (Derby / Neutral ground) over the venue header. */
function ContextChip({ icon, label }: { icon: keyof typeof Ionicons.glyphMap; label: string }) {
  const palette = useThemePalette();
  return (
    <View
      className="flex-row items-center gap-1 rounded-full bg-volt-glass px-2.5 py-1"
      style={{ borderWidth: 1, borderColor: palette.voltLine }}
    >
      <Ionicons name={icon} size={11} color={palette.voltBright} />
      <AppText variant="micro" weight="bold" className="text-volt-bright">
        {label}
      </AppText>
    </View>
  );
}

function VenueSection({
  venue,
  context,
}: {
  venue: NonNullable<NonNullable<ReturnType<typeof useMatchXg>['data']>['stats']>['venue'];
  context: NonNullable<NonNullable<ReturnType<typeof useMatchXg>['data']>['stats']>['match_context'];
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals, locale } = useLocale();
  const [imageFailed, setImageFailed] = useState(false);
  if (!venue) return null;

  const cityCountry = [venue.city, venue.country].filter(Boolean).join(', ');
  // Group thousands with the locale's separator, then map to Arabic-Indic digits.
  const capacity =
    venue.capacity > 0
      ? localizeDigits(
          new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'en-US').format(venue.capacity),
          arabicNumerals,
        )
      : null;

  const showImage = Boolean(venue.image_url) && !imageFailed;
  const isDerby = context?.is_local_derby === true;
  const isNeutral = context?.is_neutral_ground === true;

  return (
    <View>
      <View className="relative overflow-hidden bg-inset" style={{ height: 132 }}>
        {showImage ? (
          <Image
            source={{ uri: venue.image_url }}
            style={{ width: '100%', height: '100%' }}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={200}
            onError={() => setImageFailed(true)}
          />
        ) : (
          // Graceful fallback: a centred stadium glyph on the inset surface.
          <View className="flex-1 items-center justify-center">
            <Ionicons name="business-outline" size={34} color={palette.muted} />
          </View>
        )}
        {/* Chips float over the top-right of the photo (RTL flips the row start). */}
        {isDerby || isNeutral ? (
          <View className="absolute end-2.5 top-2.5 flex-row gap-2">
            {isDerby ? <ContextChip icon="flame-outline" label={t('match.matchInfo.derby')} /> : null}
            {isNeutral ? (
              <ContextChip icon="git-compare-outline" label={t('match.matchInfo.neutralGround')} />
            ) : null}
          </View>
        ) : null}
      </View>
      <View className="gap-0.5 px-4 py-3">
        <AppText variant="body" weight="bold" className="text-ink" numberOfLines={1}>
          {venue.name}
        </AppText>
        {cityCountry ? (
          <AppText variant="caption" className="text-soft" numberOfLines={1}>
            {cityCountry}
          </AppText>
        ) : null}
        {capacity ? (
          <View className="mt-1 flex-row items-center gap-1.5">
            <Ionicons name="people-outline" size={13} color={palette.muted} />
            <AppText variant="micro" weight="medium" className="text-muted" numberOfLines={1}>
              {t('match.matchInfo.capacity', { value: capacity })}
            </AppText>
          </View>
        ) : null}
      </View>
    </View>
  );
}

/**
 * Compact facts card: the basic fixture facts (kick-off / competition / round /
 * venue line) plus the richer Bzzoiro match-context — a stadium photo + venue
 * details (with derby / neutral chips) and a matchday weather strip. Each of the
 * two rich sections self-hides when its slice is null, and the whole context
 * block is absent when the xG stats haven't synced, so the card degrades to the
 * plain facts it always showed. Works for upcoming AND finished fixtures.
 */
export function MatchInfoCard({ fixture }: { fixture: FixtureWithRefs }) {
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  // The venue / weather / context ride on the xG stats row; available for an
  // upcoming match too (weather forecast + stadium), not just played ones.
  const { data: xg } = useMatchXg(fixture.id);
  const venue = xg?.stats?.venue ?? null;
  const weather = xg?.stats?.weather ?? null;
  const context = xg?.stats?.match_context ?? null;

  const kickoff = fixture.kickoff_at ? new Date(fixture.kickoff_at) : null;
  const kickoffValid = kickoff && !Number.isNaN(kickoff.getTime());

  const rows: Row[] = [
    {
      icon: 'calendar-outline',
      label: t('matches.kickoff'),
      value: kickoffValid ? formatFullDate(kickoff, numeralOptions) : '—',
    },
  ];
  if (fixture.competition?.name) {
    rows.push({
      icon: 'trophy-outline',
      label: t('match.competition'),
      value: localizeCompetition(fixture.competition.name, locale),
    });
  }
  if (fixture.round) {
    rows.push({ icon: 'flag-outline', label: t('matches.round'), value: localizeRound(fixture.round, locale) });
  }
  // The structured venue card supersedes the plain venue text row when present.
  if (fixture.venue && !venue) {
    rows.push({ icon: 'location-outline', label: t('match.venue'), value: fixture.venue });
  }

  return (
    <Card className="mx-4 mb-4 overflow-hidden">
      {venue ? <VenueSection venue={venue} context={context} /> : null}
      {rows.map((row, index) => (
        <InfoRow
          key={row.icon}
          row={row}
          // The last facts row only loses its divider when nothing follows it.
          last={index === rows.length - 1 && !weather}
        />
      ))}
      {weather ? <WeatherSection weather={weather} bordered={rows.length > 0 || Boolean(venue)} /> : null}
    </Card>
  );
}
